import {
  DashboardState,
  ECardinality,
  ECardinalityConversion,
  EDatasourceType,
  EScreenType,
  ESdChangeReason,
  ESymbolCategory,
  IAction,
  IApiMethods,
  IEvent,
  IScreenInput,
  ISymbol
} from "@kuika/kuika-cl-model";
import { EApiInputStrategy } from "@kuika/kuika-cl-model/src/enums/api-input-strategy";
import { Form } from "antd/es";
import classNames from "classnames";
import _ from "lodash";
import type { SyntheticEvent } from "react";
import localStorageService from "../../../../auth/local-storage-service";
import { IWithSDProps, withSD } from "../../../../shared/hoc/screen-design-consumer";
import { PureSdComponent } from "../../../../shared/pure-sd";
import { KMainFunctions } from "../../../../shared/utility/main-functions";
import { ScreenDesignHelper } from "../../../../shared/utility/screen-design-helper";
import { KModalFooter } from "../../../ui-kit/k-modal/k-modal-footer";
import KuikaButton from "../../../ui-kit/kuika-button/kuika-button";
import KuikaCollapsePanel from "../../../ui-kit/kuika-collapse-panel/kuika-collapse-panel";
import KuikaDropdownMenu, { KuikaDropdownMenuItemType } from "../../../ui-kit/kuika-dropdown-menu/kuika-dropdown-menu";
import KuikaInput from "../../../ui-kit/kuika-input/kuika-input";
import KuikaOverflowText from "../../../ui-kit/kuika-overflow-text/kuika-overflow-text";
import { OVERFLOW_REVEAL_HOST_CLASS } from "../../../ui-kit/kuika-overflow-text/overflow-reveal-controller";
import KuikaSelectbox, { KuikaSelectboxOption } from "../../../ui-kit/kuika-selectbox/kuika-selectbox";
import KuikaSvg from "../../../ui-kit/kuika-svg/kuika-svg";
import KuikaTabs from "../../../ui-kit/kuika-tabs/kuika-tabs";
import KuikaSmallMediumText from "../../../ui-kit/typography/SmallMediumText/SmallMediumText";
import KuikaTextMedium from "../../../ui-kit/typography/TextMedium/TextMedium";
import {
  effectiveMethodInputs,
  IElementFieldPath,
  parseElementFieldPath
} from "../../api-management/api-builder-model";
import { getParameterTypeTone } from "./action-parameter-label";

export interface IScreenInputPanelState {
  /** Whether the card header's kebab menu is open — reveals the grip-less trailing menu the same
   *  way an action card's does, and keeps it visible for as long as the menu stays open. */
  visibleMenu: boolean;
}

export interface IScreenInputPanelProps {
  screenInput: IScreenInput;
  screenInputPanelStatus: EScreenInputPanelStatus;
  descriptionEnabled?: boolean;
  onCancel?: () => void;
  onScreenInputSaved: (screenInput: IScreenInput, changeReason: ESdChangeReason) => void;
}

export enum EScreenInputPanelStatus {
  new = 1,
  edit = 2
}

/** What rc-menu hands a `KuikaDropdownMenuItemType.onClick` — only the field this file reads. */
interface IDropdownMenuClickEvent {
  domEvent?: SyntheticEvent;
}

/**
 * Renames a method input in every binding under `action`: the binding's `inputName`, and the
 * description the picker set to that same name. Returns whether anything changed, so the caller
 * knows to save the action.
 */
export const renameMethodInputBindings = (action: IAction, oldName: string, newName: string): boolean => {
  if (oldName === newName) return false;
  let changed: boolean = false;
  ScreenDesignHelper.forEachActionSymbol([action], (symbol: ISymbol): void => {
    if (symbol.description === oldName) {
      symbol.description = newName;
      changed = true;
    }
    if (symbol.inputName === oldName) {
      symbol.inputName = newName;
      changed = true;
    }
  });
  return changed;
};

/**
 * A List of Objects field is bound through the wrapper ("Items → First → Kod"), so renaming the
 * field rewrites the field path the binding reads; `inputName` stays the wrapper's. Returns
 * whether anything under `action` changed, so the caller knows to save it.
 */
export const renameListElementField = (
  action: IAction,
  wrapperName: string,
  oldName: string,
  newName: string
): boolean => {
  if (oldName === newName) return false;
  let changed: boolean = false;
  ScreenDesignHelper.forEachActionSymbol([action], (symbol: ISymbol): void => {
    if (symbol.category !== ESymbolCategory.inputMethod || symbol.inputName !== wrapperName) return;
    const path: IElementFieldPath | undefined = parseElementFieldPath(symbol.fieldName);
    if (!path || path.field !== oldName) return;
    const marker: string = path.converter !== undefined ? `[${ECardinalityConversion[path.converter]}]` : "";
    const rest: string = path.rest ? `.${path.rest}` : "";
    symbol.fieldName = `${newName}${marker}${rest}`;
    // The picker's description is "<wrapper>.<first|last>.<field>…" — the field is its third segment.
    const segments: string[] = (symbol.description ?? "").split(".");
    if (segments[0] === wrapperName && segments[2] === oldName) {
      segments[2] = newName;
      symbol.description = segments.join(".");
    }
    changed = true;
  });
  return changed;
};

/**
 * The types a screen input may declare, in the order the picker offers them.
 *
 * The id is stamped on an element INSIDE the label, not passed as `KuikaSelectboxOption.testId`.
 * Both are meant to reach the rendered row — rc-select's `OptionList` spreads
 * `pickAttrs(passedProps)` onto it — but the `testId` route did not survive to the DOM here: with
 * it, `getByTestId("screenInputTypeOption-String")` still matched 0 elements on test-master
 * (build 502) while the dropdown was provably open, its `aria-expanded` having already been
 * asserted. The label route is the one this codebase has working evidence for —
 * `role-modal.tsx` addresses its Starting Screen options exactly this way, and that locator passes.
 *
 * The id cannot live on the aria roles instead: rc-select applies those only on its NON-virtual
 * path (`!virtual ? getItemAriaProps(item, itemIndex) : {}`), and virtual scrolling is its default,
 * so the rows a user sees carry no `role="option"` at all — only a `height:0; width:0` a11y mirror
 * of the active index and its two neighbours does. `getByRole("option", { name })` can therefore
 * address a visible row in no state whatsoever (QA-423).
 *
 * `searchLabel` keeps the box searchable by the plain type name now that `label` is an element.
 */
const SCREEN_INPUT_TYPE_OPTIONS: KuikaSelectboxOption[] = [
  "Boolean",
  "DateTimeOffset",
  "Decimal",
  "Double",
  "Guid",
  "Image",
  "Int32",
  "Int64",
  "String"
].map((type: string): KuikaSelectboxOption => ({
  value: type,
  label: <span data-testid={`screenInputTypeOption-${type}`}>{type}</span>,
  searchLabel: type
}));

class ScreenInputPanel extends PureSdComponent<IScreenInputPanelProps & IWithSDProps, IScreenInputPanelState> {
  constructor(props: IScreenInputPanelProps & IWithSDProps) {
    super(props);
    this.state = { visibleMenu: false };
  }

  get isExpandedPanel(): boolean {
    const screenInputId = this.props.screenInput.id;
    const result = localStorageService.getExpandedPanelItem(screenInputId);
    return result === true;
  }

  set isExpandedPanel(value: boolean) {
    const screenInputId = this.props.screenInput.id;
    localStorageService.setExpandedPanelItem(screenInputId, value);
  }

  handleCancel = () => {
    if (this.props.onCancel) {
      this.props.onCancel();
    }
  };

  handleMenuVisibleChange = (visible: boolean): void => {
    this.setState({ visibleMenu: visible });
  };

  /** The kebab menu's "Delete" row — exactly the body's Remove/Cancel button's own handler
   *  (`handleCancel` -> `onCancel`, wired by the list to `deleteInput`/`handleCancelNew`), so the
   *  menu offers no second, divergent path to the same outcome. */
  handleDeleteMenuClick = (e: IDropdownMenuClickEvent): void => {
    e?.domEvent?.stopPropagation();
    this.setState({ visibleMenu: false });
    this.handleCancel();
  };

  getApiEvents = (): IEvent | undefined => {
    const api = this.props.sd.getSelectedApi();
    const method = this.props.sd.getSelectedApiMethod();

    if (!api || !method) {
      return;
    }
    if (method && method?.actions) {
      return method as any;
    }
  };

  getBluetoothEvents = (): IEvent | undefined => {
    const ds = this.props.sd.activeEditingDatasource;
    const bleMethod = this.props.sd.getSelectedBluetoothMethod();

    if (!(ds?.datasourceType !== EDatasourceType.BleService) || !bleMethod) {
      return;
    }

    if (bleMethod.bluetoothInfo?.actions) {
      return bleMethod.bluetoothInfo as any;
    }
  };

  handleSave = async (values) => {
    const { dashboardState } = this.props.sd;
    let screenInputs = [] as any[];
    if (dashboardState === DashboardState.apiManagement) {
      const api = this.props.sd.getSelectedApi();
      const method = this.props.sd.getSelectedApiMethod();
      if (api && method) {
        screenInputs = effectiveMethodInputs(method);
      }
    }
    if (dashboardState === DashboardState.design) {
      screenInputs = this.props.sd.getScreenInputs() as IScreenInput[];
    }
    if (screenInputs && screenInputs.length) {
      const isScreenInputExists = screenInputs.find(
        (input) => input.name === values.inputName && input.id !== this.props.screenInput.id
      );
      if (isScreenInputExists) {
        const dashboardName = dashboardState === DashboardState.apiManagement ? "Method" : "Screen";
        KMainFunctions.displayWarningNotification(
          `${dashboardName} input name "${values.inputName}" is already used before.`
        );
        return;
      }
    }
    const screenInput = {} as IScreenInput;
    screenInput.id = this.props.screenInput.id;
    screenInput.name = values.inputName.trim();
    screenInput.type = values.inputType;
    screenInput.cardinality = values.cardinality ?? 1;
    screenInput.description = this.props.descriptionEnabled ? values.description : undefined;

    const apiEvents = this.getApiEvents();
    const bleEvents = this.getBluetoothEvents();
    const oldName: string = this.props.screenInput.name;
    const newName: string = values.inputName.trim();
    // A List of Objects field is bound through its wrapper (Items → First → field), so its rename
    // lands in the bindings' field path; `inputName` names the wrapper and must stay.
    const selectedApiMethod: IApiMethods | undefined = apiEvents ? this.props.sd.getSelectedApiMethod() : undefined;
    const listWrapperName: string | undefined =
      selectedApiMethod?.inputStrategy === EApiInputStrategy.ListOfObjects
        ? selectedApiMethod.inputMethods?.[0]?.name
        : undefined;

    apiEvents?.actions?.forEach((action) => {
      const isActionUpdated: boolean =
        listWrapperName !== undefined
          ? renameListElementField(action, listWrapperName, oldName, newName)
          : renameMethodInputBindings(action, oldName, newName);

      if (isActionUpdated) {
        this.props.sd.saveActionToEvent(this.props.sd.selectedKuikaIds[0], action, apiEvents);
      }
    });

    bleEvents?.actions?.forEach((action) => {
      let isActionUpdated: boolean = false;

      action.parameters.forEach((parameter) => {
        if (parameter.symbol.description === this.props.screenInput.name) {
          parameter.symbol.description = values.inputName.trim();
          isActionUpdated = true;
        }

        if (parameter.symbol.inputName === this.props.screenInput.name) {
          parameter.symbol.inputName = values.inputName.trim();
          isActionUpdated = true;
        }
      });

      if (action.condition?.field1 && action.condition.field1.inputName === this.props.screenInput.name) {
        action.condition.field1.inputName = values.inputName.trim();
        action.condition.field1.description = values.inputName.trim();
        isActionUpdated = true;
      }

      if (action.condition?.field2 && action.condition.field2.inputName === this.props.screenInput.name) {
        action.condition.field2.inputName = values.inputName.trim();
        action.condition.field2.description = values.inputName.trim();
        isActionUpdated = true;
      }

      if (isActionUpdated) {
        this.props.sd.saveActionToEvent(this.props.sd.selectedKuikaIds[0], action, bleEvents);
      }
    });

    this.onCollapse();
    this.props.onScreenInputSaved(screenInput, ESdChangeReason.ChangeScreenInput);
  };

  changeCollapse = () => {
    if (this.isExpandedPanel === false) {
      this.onExpand();
    } else if (
      this.props.screenInput.name !== "" &&
      this.props.screenInputPanelStatus !== EScreenInputPanelStatus.new
    ) {
      this.onCollapse();
    }
  };

  onCollapse = () => {
    this.isExpandedPanel = false;
    this.forceUpdate();
  };

  onExpand = () => {
    this.isExpandedPanel = true;
    this.forceUpdate();
  };

  getActiveKey = (): string => {
    if (this.isExpandedPanel === true || this.props.screenInputPanelStatus === EScreenInputPanelStatus.new) {
      return "screen-input-panel";
    }
    return "";
  };

  convertEDataTypeToMethodInputType = (methodInput) => {
    const input = _.cloneDeep(methodInput);
    switch (input.type) {
      case 2: {
        input.type = "Boolean";
        break;
      }
      case 7: {
        input.type = "DateTime";
        break;
      }
      case 8: {
        input.type = "DateTimeOffset";
        break;
      }
      case 5: {
        input.type = "Decimal";
        break;
      }
      case 6: {
        input.type = "Double";
        break;
      }
      case 9: {
        input.type = "Guid";
        break;
      }
      case 10: {
        input.type = "Image";
        break;
      }
      case 3: {
        input.type = "Int32";
        break;
      }
      case 4: {
        input.type = "Int64";
        break;
      }
      case 1: {
        input.type = "String";
        break;
      }
    }

    return input;
  };

  render() {
    if (!this.props.screenInput) {
      return <></>;
    }

    const screenInput = this.convertEDataTypeToMethodInputType(this.props.screenInput);

    const headerText =
      this.props.screenInputPanelStatus === EScreenInputPanelStatus.new
        ? (this.props.sd.getSelectedApi() && this.props.sd.getSelectedApiMethod()) ||
          (this.props.sd.activeEditingDatasource?.datasourceType === EDatasourceType.BleService &&
            this.props.sd.getSelectedBluetoothMethod())
          ? "New Method Input"
          : this.props.sd.getScreenType() === EScreenType.Report
            ? "New Report Input"
            : "New Screen Input"
        : screenInput.name;

    // One item, running exactly what the body's Remove/Cancel button already runs (handleCancel ->
    // onCancel), so the kebab offers no second, divergent delete path.
    const screenInputMenu: KuikaDropdownMenuItemType[] = [
      {
        key: "delete",
        label: "Delete",
        dataTestId: "screenInputDeleteMenuItem",
        danger: true,
        onClick: this.handleDeleteMenuClick
      }
    ];

    return (
      <div data-testid="screenInputPanel">
        <KuikaCollapsePanel
          onChange={this.changeCollapse}
          defaultActiveKey={this.getActiveKey()}
          activeKey={this.getActiveKey()}
          iconPlacement="right"
          className="kuika-right-panel__collapse kuika-right-panel__collapse--boxed kuika-right-panel__collapse--screen-input"
          items={[
            {
              key: "screen-input-panel",
              label: (
                <div
                  className={classNames(OVERFLOW_REVEAL_HOST_CLASS, {
                    "kuika-action-card--menu-open": this.state.visibleMenu
                  })}
                  style={{ display: "flex", justifyContent: "space-between", alignItems: "center", width: "100%" }}
                >
                  <div className="kuika-action-card__lead">
                    <KuikaTextMedium
                      className="kuika-action-card__title"
                      data-onboarding-id="screen-input-panel-header-title"
                      data-testid="screenInputPanelTitle"
                      title={headerText}
                    >
                      {/* A long input name fades and slides on hover like an action card's title,
                          instead of being cut off hard by the header. */}
                      <KuikaOverflowText>{headerText}</KuikaOverflowText>
                    </KuikaTextMedium>
                  </div>
                  <div className="kuika-action-card__trail">
                    {/* No chip for a draft input — its type is only chosen inside the still-open
                        form below, and the header re-renders from `screenInput`, not form state. */}
                    {screenInput?.type && (
                      <span
                        className={`kuika-action-section__param-chip kuika-action-section__param-chip--${getParameterTypeTone(
                          screenInput.type
                        )}`}
                      >
                        {screenInput.type}
                      </span>
                    )}
                    <div
                      className="kuika-action-card__menu"
                      onClick={(e) => {
                        e.stopPropagation();
                      }}
                    >
                      <KuikaDropdownMenu
                        setOpen={this.handleMenuVisibleChange}
                        open={this.state.visibleMenu}
                        options={screenInputMenu}
                        trigger="click"
                      >
                        <KuikaButton
                          type="tertiary"
                          size="sm"
                          isIcon
                          color="neutral"
                          data-testid="screenInputMenuButton"
                        >
                          <KuikaSvg name="dots-vertical" type="styling-panel-icons" width={14} height={14} />
                        </KuikaButton>
                      </KuikaDropdownMenu>
                    </div>
                  </div>
                </div>
              ),
              children: (
                <div className="kuika-right-panel__collapse-body">
                  <div className="kuika_styling_typography_wrapper kuika_screen-input__wrapper">
                    <Form
                      name="basic"
                      onFinish={this.handleSave}
                      initialValues={{
                        inputName: screenInput?.name,
                        inputType: screenInput?.type,
                        cardinality: screenInput?.cardinality,
                        description: this.props.descriptionEnabled ? screenInput?.description : undefined
                      }}
                    >
                      <KuikaSmallMediumText>Input Name</KuikaSmallMediumText>
                      <Form.Item
                        name="inputName"
                        rules={[
                          { required: true, message: "A name is required!" },
                          {
                            validator: (_, value) => {
                              if (value.includes(" ")) {
                                return Promise.reject(new Error("Spaces are not allowed"));
                              }
                              return Promise.resolve();
                            }
                          },
                          { pattern: /^[A-Za-z][A-Za-z0-9]+$/, message: "Field accepts only english letters." }
                        ]}
                      >
                        <KuikaInput style={{ width: "100%" }} autoFocus data-testid="screenInputNameInput"></KuikaInput>
                      </Form.Item>

                      <KuikaSmallMediumText>Type</KuikaSmallMediumText>
                      <div className="kuika_property-grid-column" style={{ padding: 0 }}>
                        <Form.Item name="inputType" rules={[{ required: true, message: "A type is required!" }]}>
                          <KuikaSelectbox
                            placeholder="Select a type"
                            searchable
                            options={SCREEN_INPUT_TYPE_OPTIONS}
                            data-testid="screenInputTypeSelect"
                          />
                        </Form.Item>
                      </div>

                      {this.props.descriptionEnabled && (
                        <>
                          <KuikaSmallMediumText>Description</KuikaSmallMediumText>
                          <div className="kuika_property-grid-column" style={{ padding: 0 }}>
                            <Form.Item name="description">
                              <KuikaInput
                                type="textarea"
                                style={{ width: "100%", background: "none" }}
                                autoSize={{ minRows: 2, maxRows: 4 }}
                              />
                            </Form.Item>
                          </div>
                        </>
                      )}

                      <div style={{ display: "none" }}>
                        <KuikaSmallMediumText>Cardinality</KuikaSmallMediumText>

                        <Form.Item name="cardinality">
                          <KuikaTabs
                            options={["One", "Many"]}
                            onChange={(value) => {
                              return value === "One" ? ECardinality.One : ECardinality.Many;
                            }}
                            value={screenInput?.cardinality === ECardinality.Many ? "Many" : "One"}
                          />
                        </Form.Item>
                      </div>

                      {/* The dialogs' own decision row: Cancel beside the commit on a new input; Remove,
                          being destructive, at the far end from Update on an existing one. The commit
                          has no handler, so it submits this Form. */}
                      {this.props.screenInputPanelStatus === EScreenInputPanelStatus.new ? (
                        <KModalFooter
                          onCancel={this.handleCancel}
                          cancelTestId="screenInputRemoveButton"
                          submitLabel="Create"
                          submitTestId="screenInputSubmitButton"
                        />
                      ) : (
                        <KModalFooter
                          onDelete={this.handleCancel}
                          deleteLabel="Remove"
                          deleteTestId="screenInputRemoveButton"
                          submitLabel="Update"
                          submitTestId="screenInputSubmitButton"
                        />
                      )}
                    </Form>
                  </div>
                </div>
              )
            }
          ]}
        />
      </div>
    );
  }

  checkNeedRender = (changeReason: ESdChangeReason): boolean => {
    if (
      changeReason === ESdChangeReason.ChangeSelectedDiByCanvasFrame ||
      changeReason === ESdChangeReason.ChangeSelectedDiByTools ||
      changeReason === ESdChangeReason.DragDropDi ||
      changeReason === ESdChangeReason.DragDropBlock
    ) {
      return true;
    }
    return false;
  };
}

const screenInputPanel = withSD<IScreenInputPanelProps>(ScreenInputPanel);
export { screenInputPanel as ScreenInputPanel };
